import React from "react"
import PropTypes from "prop-types"
import classNames from "classnames"

// helpers
import { checkHasPermission } from "shared/profiles/tabs/helperFunctions"
import { getCustomFieldGroupArray } from "app/static/frontend/profiles-new/helperFunctions"

// style
import DataItemWrapper from "app/static/frontend/profiles-new/components/DataTableView/DataItemWrapper"
import {
    GenericTitleSection,
    DlcTitleSection,
} from "app/static/frontend/profiles-new/components/DataTableView/TitleSections"
import "app/static/frontend/profiles-new/components/DataTableView/index.styl"
import { Button, CounterMessage, DTVGrid, DTVSectionHeader } from "quorumdesign"
import TooltipInfoCircle from "components/TooltipInfoCircle"
// constants
import { CUSTOM_FIELDS_TITLE } from "app/static/frontend/profiles-new/constants"

import { isFeatureEnabled } from "shared/featureflags/helperFunctions"

export const DataTableView = ({
    title,
    data,
    profileId,
    parentWidth,
    canEditSection,
    editProfileFunction,
    hideTitle,
    numOverflowElements,
    dlcWidth,
    elementId,
}) => (
    <div
        className={classNames(
            "section-container",
            { "dlc-section-component": dlcWidth },
            { "section-component": !dlcWidth },
        )}
    >
        {!hideTitle && !dlcWidth && (
            <GenericTitleSection
                title={title}
                canEditSection={canEditSection}
                editProfileFunction={editProfileFunction}
            />
        )}
        <div className="section-values">
            {
                // we need
                !hideTitle && dlcWidth && <DlcTitleSection dlcWidth={dlcWidth} title={title} />
            }
            {data.map((dataObj, idx) => {
                const DataItem = dataObj.dataItem || DataItemWrapper

                // filter out by permissions if it exists from the selector declaration
                return (
                    (!dataObj.permissions || !!(dataObj.permissions && checkHasPermission(dataObj.permissions))) && (
                        <DataItem
                            title={dataObj.title}
                            subtitle={dataObj.subtitle}
                            displayValue={dataObj.displayValue}
                            onClick={dataObj.onPress}
                            width={dataObj.width}
                            isLink={dataObj.isLink}
                            profileId={profileId}
                            tooltipText={dataObj.tooltipText}
                            // if we need to seg to another object, pass the id
                            segueId={dataObj.segueId}
                            segueUrl={dataObj.segueUrl}
                            parentWidth={parentWidth}
                            // determine whether the social item is a campaign
                            isCampaign={dataObj.isCampaign}
                            visible={!numOverflowElements || data.length - idx > numOverflowElements}
                            dlcWidth={dlcWidth}
                            elementId={elementId}
                        />
                    )
                )
            })}
        </div>
    </div>
)

DataTableView.propTypes = {
    data: PropTypes.array,
    title: PropTypes.string,
    parentWidth: PropTypes.number,
    profileId: PropTypes.number.isRequired,
    canEditSection: PropTypes.bool,
    editProfileFunction: PropTypes.func,
    hideTitle: PropTypes.bool,
    // the number of elements that are over flowing in the container
    // we render these with visible = false so that they don't show up but can
    // still be used when calculating the overflow amounts in DisplayElement for DLC
    numOverflowElements: PropTypes.number,
    dlcWidth: PropTypes.number,
    /** the element id name used in the DLC table */
    elementId: PropTypes.string,
}

DataTableView.defaultProps = {
    hideTitle: false,
}

export default DataTableView

export const DesignSystemDataTableView = ({
    title,
    data,
    profileId,
    parentWidth,
    canEditSection,
    editProfileFunction,
    headerButtons,
    hideTitle,
    tooltipText,
    icon,
    isCustom,
    numOverflowElements,
    dlcWidth,
    gridWidth,
    collapsible,
    collapsedGridRows,
    elementId,
    truncateText,
}) => {
    const renderDataItem = (dataObj, idx) => {
        const DataItem = dataObj.dataItem || DataItemWrapper

        // filter out by permissions if it exists from the selector declaration
        return (
            (!dataObj.permissions || !!(dataObj.permissions && checkHasPermission(dataObj.permissions))) && (
                <DataItem
                    title={dataObj.title}
                    subtitle={dataObj.subtitle}
                    displayValue={dataObj.displayValue}
                    onClick={dataObj.onPress}
                    width={dataObj.width}
                    isLink={dataObj.isLink}
                    profileId={profileId}
                    tooltipText={dataObj.tooltipText}
                    // if we need to seg to another object, pass the id
                    segueId={dataObj.segueId}
                    segueUrl={dataObj.segueUrl}
                    externalHyperlinkURL={dataObj.externalHyperlinkURL}
                    parentWidth={parentWidth}
                    // determine whether the social item is a campaign
                    isCampaign={dataObj.isCampaign}
                    visible={!numOverflowElements || data.length - idx > numOverflowElements}
                    dlcWidth={dlcWidth}
                    style={dataObj.style}
                    collapseEmptySpace={dataObj.collapseEmptySpace}
                    elementId={`${elementId}`}
                    truncateText={truncateText}
                />
            )
        )
    }

    const cf_groups = title === CUSTOM_FIELDS_TITLE && getCustomFieldGroupArray(data)

    if (cf_groups) {
        return (
            <React.Fragment>
                <DTVSectionHeader
                    headerText={!hideTitle && title}
                    headerButtons={[
                        canEditSection && (
                            <Button text="Edit" leftIcon="pencil" type="outlined" onClick={editProfileFunction} />
                        ),
                    ]}
                />
                {cf_groups.map((groupObj) => (
                    <DTVGrid
                        headerComponent={
                            <CounterMessage icon="layer-group" iconFamily="far">
                                {groupObj.name}
                            </CounterMessage>
                        }
                        gridWidth={gridWidth || 12}
                        collapsible={collapsible}
                        collapsedGridRows={collapsedGridRows}
                    >
                        {groupObj.custom_fields.map((dataObj, idx) => renderDataItem(dataObj, idx))}
                    </DTVGrid>
                ))}
            </React.Fragment>
        )
    }

    return (
        <DTVGrid
            headerText={!hideTitle && <span className="new-tab-title">{title}</span>}
            headerComponent={tooltipText && <TooltipInfoCircle tooltipText={tooltipText} />}
            icon={icon}
            gridWidth={isCustom ? null : gridWidth || 12}
            collapsible={collapsible}
            collapsedGridRows={collapsedGridRows}
            headerButtons={[
                canEditSection && (
                    <Button text="Edit" leftIcon="pencil" type="outlined" onClick={editProfileFunction} />
                ),
                ...(headerButtons || []),
            ]}
        >
            {isCustom &&
            isFeatureEnabled("ff_custom_official_social_media") &&
            !Userdata.Permissions.qp_contacts_documents ? (
                <div>
                    <div>
                        Power up your tracking by adding the custom social media monitoring add-on to stay ahead of the
                        curve.
                    </div>
                    {/* TODO: update the link with the social media custom monitoring landing page once its ready
                    <a href="https://www.quorum.us/products/news-monitoring/" target="_blank" rel="noreferrer">
                        Click here to learn more.
                    </a> */}
                </div>
            ) : (
                data.map((dataObj, idx) => renderDataItem(dataObj, idx))
            )}
        </DTVGrid>
    )
}

DesignSystemDataTableView.propTypes = {
    data: PropTypes.array,
    icon: PropTypes.string,
    title: PropTypes.string,
    parentWidth: PropTypes.number,
    profileId: PropTypes.number.isRequired,
    canEditSection: PropTypes.bool,
    editProfileFunction: PropTypes.func,
    headerButtons: PropTypes.arrayOf(PropTypes.func),
    hideTitle: PropTypes.bool,
    // the number of elements that are over flowing in the container
    // we render these with visible = false so that they don't show up but can
    // still be used when calculating the overflow amounts in DisplayElement for DLC
    numOverflowElements: PropTypes.number,
    dlcWidth: PropTypes.number,
    // how many DTV items across should be shown
    // ideally, this would be set in the same place as the DTV item style because they are related
    // (i.e. style and grid width should both be set on the DataTableView or both set on the DataItem/Wrapper)
    // but architecturally the DataItem is independent of the DataTableView
    gridWidth: PropTypes.number,
    /** the element id name used in the DLC table */
    elementId: PropTypes.string,
}

DesignSystemDataTableView.defaultProps = {
    hideTitle: false,
}
